import React from "react";
import styled, { ThemeProvider } from "styled-components";
import { lightTheme } from "./Themes";
import { Design, Develope } from "./AllSvgs";
import { motion } from 'framer-motion';

import LogoComponent from "../subComponents/LogoComponent";
import SocialIcons from "../subComponents/SocialIcons";
import PowerButton from "../subComponents/PowerButton";
import ParticleComponent from "../subComponents/ParticleComponent";
import BigTitle from "../subComponents/BigTitle"

const Box = styled.div`
  background-color: ${(props) => props.theme.body};
  width: 100vw;
  height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 5rem;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch; /* For smooth scrolling on iOS */

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.body};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.text};
    border-radius: 4px;
    border: 2px solid ${props => props.theme.body};
  }

  @media (max-width: 1200px) {
    padding-top: 4rem;
  }

  @media (max-width: 768px) {
    justify-content: flex-end;
    height: auto;
    min-height: 100vh;
    padding-top: 5rem;
    padding-bottom: 2rem;
  }

  @media (max-width: 480px) {
    padding-top: 8rem;
    padding-bottom: 4rem;
  }
`;

const SocialWrapper = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;

  @media (max-width: 768px) {
    position: fixed;
    bottom: 1rem;
    left: 1rem;
    top: auto;
  }

  @media (max-width: 480px) {
    bottom: 0.5rem;
    left: 0.5rem;
  }
`;

const TopRightWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  z-index: 11;
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 2rem;
  margin-top: 1.5rem;
  padding-bottom: 2.5rem;
  width: calc(100% - 4rem);
  max-width: 1200px;
  margin-left: 4rem;
  box-sizing: border-box;
  overflow: visible;
  flex: 1;

  @media (max-width: 1200px) {
    padding: 0 1.5rem 2.5rem 1.5rem;
    margin-left: 3rem;
    width: calc(100% - 3rem);
  }

  @media (max-width: 768px) {
    padding: 0 1rem 4rem 1rem;
    margin-top: 2.5rem;
    margin-left: 0;
    width: 100%;
  }

  @media (max-width: 600px) {
    padding: 0 0.2rem 3rem 0.2rem;
    margin-top: 2rem;
    gap: 1rem;
    width: 100vw;
    max-width: 100vw;
    margin-left: 0;
    overflow-x: hidden;
  }
`;

const CardsWrapper = styled(motion.div)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 2rem;
  width: 100%;
  max-width: 1400px;
  margin: 1.5rem auto 0 auto;
  box-sizing: border-box;

  @media (min-width: 1400px) {
    gap: 3rem;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
    margin-top: 2rem;
  }
  @media (max-width: 600px) {
    width: 100vw;
    max-width: 100vw;
    margin: 1.5rem 0 0 0;
    padding: 0;
    gap: 1.2rem;
    overflow-x: hidden;
  }
`;

const Description = styled.div`
  color: inherit;
  font-size: calc(0.6em + 0.8vw);
  padding: 0.3rem 0;
  transition: color 0.3s ease-in-out;

  @media (min-width: 1400px) {
    font-size: calc(0.6em + 0.6vw);
  }

  strong {
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    display: block;
    margin-top: 0.5rem;
    color: inherit;
  }

  ul,
  p {
    margin-left: 1rem;
    color: inherit;
  }

  li {
    color: inherit;
  }

  @media (max-width: 480px) {
    font-size: calc(0.55em + 0.8vw);
    
    ul, p {
      margin-left: 0.5rem;
    }
  }
`;

const CardTitle = styled.h2`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 1.3rem;
  margin-bottom: 0.4rem;
  font-family: inherit;
  color: inherit;
  
  & > * {
    margin-right: 0.6rem;
    fill: currentColor;
    width: 30px;
    height: 30px;
  }

  @media (min-width: 1400px) {
    font-size: 1.5rem;
    margin-bottom: 0.6rem;
    
    & > * {
      width: 35px;
      height: 35px;
    }
  }

  @media (max-width: 900px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 600px) {
    font-size: 1rem;
    margin-bottom: 0.2rem;
  }
`;

const MotionSkillCard = styled(motion.div)`
  border: 2px solid ${(props) => props.theme.text};
  color: ${(props) => props.theme.text};
  background-color: ${(props) => props.theme.body};
  padding: 2rem 1.8rem;
  width: 45%;
  max-width: 600px;
  min-width: 300px;
  min-height: auto;
  max-height: none;
  overflow: visible;
  z-index: 2;
  line-height: 1.3;
  cursor: pointer;
  font-family: "Ubuntu Mono", monospace;
  font-size: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.3s ease-in-out;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  text-align: left;
  box-sizing: border-box;
  gap: 0.8rem;
  position: relative;
  overflow-wrap: break-word;
  word-break: break-word;

  @media (min-width: 1400px) {
    padding: 2.5rem 2.2rem;
    font-size: 1.1rem;
    gap: 1rem;
  }

  &:hover {
    color: ${(props) => props.theme.body};
    background-color: ${(props) => props.theme.text};
    transform: translateY(-5px);
    box-shadow: 0px 8px 15px rgba(0, 0, 0, 0.2);
    
    /* Add hover styles for SVG icons */
    ${CardTitle} > * {
      fill: ${(props) => props.theme.body};
    }
  }

  @media (max-width: 900px) {
    width: 280px;
    padding: 1.5rem 1rem;
    font-size: 0.95rem;
    min-width: 0;
  }
  @media (max-width: 600px) {
    padding: 0.7rem 0.5rem;
    font-size: 0.8rem;
    width: 75vw;
    max-width: 80vw;
    gap: 0.5rem;
  }
`;

const SectionHeading = styled.div`
  font-weight: bold;
  font-size: 1em;
  margin-top: 0.5rem;
  margin-bottom: 0.2rem;
  font-family: inherit;

  @media (max-width: 600px) {
    font-size: 0.95em;
    margin-top: 0.4rem;
  }
`;

const Mono = styled.span`
  font-family: inherit;
  font-size: 1em;
  line-height: 1.4;

  @media (max-width: 600px) {
    line-height: 1.3;
  }
`;

const Main = styled.div`
  border: 2px solid ${(props) => props.theme.text};
  color: ${(props) => props.theme.text};
  background-color: ${(props) => props.theme.body};
  padding: 1.5rem;
  width: 70vw;
  max-width: 800px;
  min-height: auto;
  max-height: none;
  overflow: visible;
  z-index: 2;
  line-height: 1.3;
  cursor: pointer;
  font-family: "Ubuntu Mono", monospace;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.3s ease-in-out;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  text-align: center;
  box-sizing: border-box;

  &:hover {
    color: ${(props) => props.theme.body};
    background-color: ${(props) => props.theme.text};
    transform: translateY(-5px);
    box-shadow: 0px 8px 15px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 1200px) {
    width: 75vw;
    padding: 1.2rem;
  }

  @media (max-width: 768px) {
    width: 70vw;
    padding: 1rem;
  }

  @media (max-width: 480px) {
    width: 75vw;
    padding: 1rem 0.8rem;
  }
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: calc(1em + 1vw);
  margin-bottom: 1rem;

  ${Main}:hover & {
    & > * {
      fill: ${(props) => props.theme.body};
    }
  }

  & > *:first-child {
    margin-right: 0.5rem;
  }

  @media (max-width: 480px) {
    font-size: calc(0.9em + 1vw);
  }
`;

const BigTitleWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none; /* Prevent blocking interactions */
  
  @media (max-width: 768px) {
    display: block;
    top: 1rem;
    right: 1rem;
    transform: scale(0.8);
    transform-origin: right top;
  }
  
  @media (max-width: 480px) {
    display: block;
    top: 2rem;
    right: 0.5rem;
    transform: scale(0.7);
    transform-origin: right top;
  }
`;

// Animation variants
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
      delayChildren: 0.2
    }
  }
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 50
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 12,
      duration: 0.6
    }
  }
};

const cardHover = {
  scale: 1.05,
  y: -8,
  transition: {
    type: 'spring',
    stiffness: 300,
    damping: 15
  }
};

const MySkillsPage = () => {
  return (
    <ThemeProvider theme={lightTheme}>
      <Box>
        <LogoComponent theme="light" />
        <PowerButton />
        <ParticleComponent theme="light" />
        <SocialWrapper>
          <SocialIcons theme="light" />
        </SocialWrapper>
        <ContentWrapper style={{ padding: 0, marginLeft: 0, width: '100%' }}>
          <CardsWrapper
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* <MotionSkillCard
              variants={cardVariants}
            >
              <CardTitle>
                <Develope /> Software Developer
              </CardTitle>

              <Description>
                I enjoy solving real-world problems through thoughtful design and clean development.
              </Description>

              <Description>
                <strong>Skills</strong>
                <p>Python, JavaScript, TypeScript, Node.js, React, Next.js, MongoDB, Express.js, HTML5, CSS3</p>
              </Description>

              <Description>
                <strong>Tools</strong>
                <p>VS Code, Git & GitHub, Postman, Jupyter Notebook, Anaconda, PyCharm, Figma, Canva</p>
              </Description>

              <Description>
                <strong>Areas of Interest</strong>
                <p>Full Stack Development, UI/UX Design, Machine Learning, API Integration, Productivity Tools</p>
              </Description>


            </MotionSkillCard> */}
            <MotionSkillCard
              variants={cardVariants}
            >
              <CardTitle>
                <Design /> Fullstack Developer
              </CardTitle>

              <Description>
                I'm passionate about building scalable, user-friendly applications and continuously learning new technologies.
              </Description>

              <Description>
                <strong>I like to Develop</strong>
                <ul>
                  <li>Web Apps</li>
                  <li>Responsive Websites</li>
                  <li>Modern UI Interfaces</li>
                </ul>
              </Description>

              <Description>
                <strong>Skills</strong>
                <ul>
                  <li>JavaScript, Python, TypeScript</li>
                  <li>React, Next.js, Node.js, Flask</li>
                  <li>MongoDB, Firebase, Express</li>
                  <li>HTML, CSS, Tailwind, Bootstrap</li>
                </ul>
              </Description>

              <Description>
                <strong>Tools</strong>
                <ul>
                  <li>VS Code, Git & GitHub</li>
                  <li>Figma, Canva, Postman</li>
                  <li>Vercel, Netlify, Jupyter</li>
                </ul>
              </Description>


            </MotionSkillCard>
          </CardsWrapper>
        </ContentWrapper>
        <BigTitleWrapper>
          <BigTitle text='SKILLS' top="5%" right="10%" />
        </BigTitleWrapper>
      </Box>
    </ThemeProvider>
  );
};

export default MySkillsPage;
